import axiosInstance from '@/utils/axiosInstance';
import { API_URL, USER_ROLES } from '@/constant/constant';

const getAuthHeaders = (vendorId) => ({
  sessionToken: localStorage.getItem('sessionToken'),
  'X-USER-ROLE': USER_ROLES.VENDOR,
  'X-Vendor-Id': vendorId,
});

/**
 * Fetch all delivery slabs for a vendor
 * @param {string} vendorId - The vendor ID
 * @returns {Promise<Array>} Array of delivery slabs
 */
export const fetchDeliverySlabs = async (vendorId) => {
  try {
    if (!vendorId) {
      console.warn('vendorId is required to fetch delivery slabs');
      return [];
    }

    const response = await axiosInstance.get(
      `${API_URL.BASE_VENDOR}/rest/big-local/api/v1/vendor/${vendorId}/delivery-slabs?vendorId=${vendorId}`,
      {
        headers: getAuthHeaders(vendorId),
      }
    );
    return Array.isArray(response.data) ? response.data : [];
  } catch (error) {
    console.error('Error fetching delivery slabs:', error);
    throw error;
  }
};

/**
 * Create a new delivery slab for a vendor
 * @param {string} vendorId - The vendor ID
 * @param {Array} slabs - Array of slab objects with minKm, maxKm, charge
 * @returns {Promise<Object>} Created slab data
 */
export const createDeliverySlab = async (vendorId, slabs) => {
  try {
    if (!vendorId) {
      throw new Error('vendorId is required');
    }

    const response = await axiosInstance.post(
      `${API_URL.BASE_VENDOR}/rest/big-local/api/v1/vendor/${vendorId}/delivery-slabs?vendorId=${vendorId}`,
      slabs,
      {
        headers: getAuthHeaders(vendorId),
      }
    );
    return response.data;
  } catch (error) {
    console.error('Error creating delivery slab:', error);
    throw error;
  }
};

/**
 * Update an existing delivery slab
 * @param {string} vendorId - The vendor ID
 * @param {string} slabId - The slab ID to update
 * @param {Object} payload - Updated slab data (minKm, maxKm, charge)
 * @returns {Promise<Object>} Updated slab data
 */
export const updateDeliverySlab = async (vendorId, slabId, payload) => {
  try {
    if (!vendorId || !slabId) {
      throw new Error('vendorId and slabId are required');
    }

    const response = await axiosInstance.put(
      `${API_URL.BASE_VENDOR}/rest/big-local/api/v1/vendor/delivery-slab/${slabId}?vendorId=${vendorId}`,
      payload,
      {
        headers: getAuthHeaders(vendorId),
      }
    );
    return response.data;
  } catch (error) {
    console.error('Error updating delivery slab:', error);
    throw error;
  }
};

/**
 * Delete a delivery slab
 * @param {string} vendorId - The vendor ID
 * @param {string} slabId - The slab ID to delete
 * @returns {Promise<Object>} Response data
 */
export const deleteDeliverySlab = async (vendorId, slabId) => {
  try {
    if (!vendorId || !slabId) {
      throw new Error('vendorId and slabId are required');
    }

    const response = await axiosInstance.delete(
      `${API_URL.BASE_VENDOR}/rest/big-local/api/v1/vendor/${vendorId}/delivery-slab/${slabId}?vendorId=${vendorId}`,
      {
        headers: getAuthHeaders(vendorId),
      }
    );
    return response.data;
  } catch (error) {
    console.error('Error deleting delivery slab:', error);
    throw error;
  }
};

/**
 * Validate delivery slabs for overlapping ranges
 * @param {Object} slab - Slab to validate
 * @param {Array} otherSlabs - Other slabs to check against
 * @param {string} editingId - ID of slab being edited (if any)
 * @returns {Object} Validation result with isValid and message
 */
export const validateDeliverySlab = (slab, otherSlabs = [], editingId = null) => {
  const min = parseFloat(slab.minKm);
  const max = parseFloat(slab.maxKm);
  const charge = parseFloat(slab.charge);

  if (Number.isNaN(min) || min < 0) {
    return { isValid: false, message: 'Valid minimum distance is required' };
  }

  if (Number.isNaN(max) || max < 0) {
    return { isValid: false, message: 'Valid maximum distance is required' };
  }

  if (min >= max) {
    return {
      isValid: false,
      message: 'Maximum distance must be greater than minimum distance',
    };
  }

  if (Number.isNaN(charge) || charge < 0) {
    return { isValid: false, message: 'Valid delivery charge is required' };
  }

  // Check for overlapping ranges
  const hasOverlap = otherSlabs.some((other) => {
    if (other.id === editingId) return false;
    return max > other.minKm && min < other.maxKm;
  });

  if (hasOverlap) {
    return { isValid: false, message: 'Slab ranges must not overlap' };
  }

  return { isValid: true, message: 'Slab is valid' };
};
